import type { Locale } from "@/i18n";

interface LadderStepCopy {
  step: string;
  name: string;
  duration: string;
  description: string;
}

interface LadderTrack {
  intro: string;
  steps: readonly LadderStepCopy[];
}

interface LadderCopy {
  sectionLabel: string;
  engagementStatement: string;
  tracks: readonly LadderTrack[];
  footnote: string;
}

const en: LadderCopy = {
  sectionLabel: "How We Work",
  engagementStatement:
    "We do not sell hours: we take a well-defined task whole, on our own schedule and with our own resources, so you never have to staff a project manager next to us. You get the outcome, with a defined scope, a fixed price, and an end date. On the build, where savings are clearly measurable, part of that price can be a success fee: we take a share of the risk too. We also work as a subcontractor alongside prime contractors, on the same terms: a delimited work package, our own responsibility.",
  tracks: [
    {
      intro: "If you already have a build in mind, it is three steps:",
      steps: [
        {
          step: "01",
          name: "Scope",
          duration: "free until the quote",
          description:
            "We walk through the task and write down what is in and what is out. That scope gets a fixed-price quote with a date on it, and no hourly billing.",
        },
        {
          step: "02",
          name: "Build",
          duration: "per the agreed scope · fixed price",
          description:
            "We build it, on our own schedule and with our own resources. You get working, demonstrable increments rather than daily presence.",
        },
        {
          step: "03",
          name: "Handoff",
          duration: "at project close",
          description:
            "Production, documentation, knowledge transfer. The code and the knowledge are yours: no retainer dependency, your team can carry it on alone.",
        },
      ],
    },
    {
      intro:
        "If you are still looking for where AI would help, we put two steps in front of the build. You can stop after either one with the result in hand:",
      steps: [
        {
          step: "+01",
          name: "AI Opportunity Audit",
          duration: "scoped up front · fixed price",
          description:
            "We walk one workflow end to end: how many manual steps it holds, how much time goes into it, where errors appear. You get a document on what is worth handing to AI, what is not, what it would return in your own numbers, and in what order to build it. The document is yours to keep, useful even if you build with someone else.",
        },
        {
          step: "+02",
          name: "Proof of Value",
          duration: "4 to 6 weeks · fixed price",
          description:
            "A working prototype on your real data. Success criteria are agreed up front and measured at the end: you find out whether the audit's estimate holds before committing to the full build.",
        },
      ],
    },
  ],
  footnote:
    "Each step de-risks the next, and the build then follows the three steps above. The durations are indicative; your case may differ, prices on request. Price and date always apply to the scope we agreed on: if the work grows, it gets its own quote, so neither side ends up cornered.",
};

const hu: LadderCopy = {
  sectionLabel: "Hogyan dolgozunk",
  engagementStatement:
    "Nem órákat adunk el: a jól meghatározott feladatot egészben vesszük át, saját időbeosztással és erőforrásokkal, így nem kell külön projektmenedzsert állítanod mellénk. Te az eredményt kapod: rögzített terjedelemmel, fix áron, határidővel. Ha a megtakarítás jól mérhető, a fejlesztésnél a fix ár egy része sikerdíj is lehet, vagyis a kockázatból mi is vállalunk egy részt. Fővállalkozók mellett alvállalkozóként ugyanígy dolgozunk: körülhatárolt munkacsomagot vállalunk, a saját felelősségünkre.",
  tracks: [
    {
      intro: "Ha van egy fejlesztési feladatod, három lépés az egész:",
      steps: [
        {
          step: "01",
          name: "Terjedelem",
          duration: "az ajánlatig díjmentes",
          description:
            "Átbeszéljük a feladatot, és leírjuk, mi tartozik bele és mi nem. Erre adunk fix árat és határidőt, óradíj nélkül.",
        },
        {
          step: "02",
          name: "Fejlesztés",
          duration: "terjedelem szerint · fix ár",
          description:
            "Megépítjük, saját időbeosztással és erőforrásokkal. Nem napi jelenlétet kapsz, hanem működő, bemutatható részeredményeket.",
        },
        {
          step: "03",
          name: "Átadás",
          duration: "a projekt zárásakor",
          description:
            "Üzembe állítás, dokumentáció, tudásátadás. A kód és a tudás a tiéd, és nem kötsz velünk örökös támogatási szerződést: a csapatod önállóan tovább tudja vinni.",
        },
      ],
    },
    {
      intro:
        "Ha viszont még csak keresed, hol segíthet az AI, két lépést teszünk a fejlesztés elé. Bármelyik után megállhatsz, az addigi eredmény akkor is a tiéd:",
      steps: [
        {
          step: "+01",
          name: "AI-audit",
          duration: "terjedelem szerint · fix ár",
          description:
            "Egy munkafolyamatodat elejétől a végéig átnézzük: hány kézi lépés van benne, mennyi idő megy rá, hol keletkezik hiba. Kapsz egy dokumentumot arról, mit érdemes AI-ra bízni, mit nem, mennyit hozna ez a te számaid alapján, és milyen sorrendben építsük meg. A dokumentum a tiéd marad, akkor is, ha végül mással építed meg.",
        },
        {
          step: "+02",
          name: "Prototípus",
          duration: "4-6 hét · fix ár",
          description:
            "A kiválasztott feladatra működő prototípust építünk, a te valódi adataidon. Előre közösen rögzítjük, mit tekintünk sikernek, és a végén megmérjük: még a nagy fejlesztés előtt kiderül, tartja-e magát az audit becslése.",
        },
      ],
    },
  ],
  footnote:
    "Minden lépés csökkenti a következő kockázatát, a fejlesztés pedig ezután a fenti három lépés szerint halad. A megadott időtartamok tájékoztató jellegűek, a te eseted eltérhet ettől; pontos árat a feladat ismeretében mondunk. Az árat és a határidőt mindig a közösen rögzített terjedelemre adjuk: ha közben nő a feladat, arra új ajánlatot írunk, így egyik felet sem szorítja rossz alkuba.",
};

export const LADDER_COPY: Record<Locale, LadderCopy> = {
  en,
  hu,
};
